import type { ComponentProps } from "react";
import { cn } from "../utils";

const NAMES: Record<string, string> = {
  mercadolivre: "Mercado Livre",
  shopee: "Shopee",
};

const MARKS: Record<string, string> = {
  mercadolivre: "ML",
  shopee: "SP",
};

export function channelTone(provider: string) {
  return provider === "shopee"
    ? "bg-channel-shopee-subtle text-channel-shopee"
    : "bg-channel-ml-subtle text-channel-ml";
}

export function ChannelMark({
  provider,
  size = "sm",
  className,
}: {
  provider: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const name = NAMES[provider] ?? "Canal";
  return (
    <span
      aria-label={name}
      title={name}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded font-semibold tracking-wide",
        size === "md" ? "h-5 min-w-5 px-1 text-2xs" : "h-4 min-w-4 px-0.5 text-[0.5625rem]",
        channelTone(provider),
        className,
      )}
    >
      {MARKS[provider] ?? "•"}
    </span>
  );
}

export function ChannelBadge({
  provider,
  nickname,
  className,
}: {
  provider: string;
  nickname?: string | null;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 text-2xs text-ink-secondary",
        className,
      )}
    >
      <ChannelMark provider={provider} />
      <span className="truncate">{nickname || NAMES[provider] || "Loja"}</span>
    </span>
  );
}

export function ChannelFilter({
  value,
  channels,
  onChange,
  className,
}: {
  value: string | undefined;
  channels: Array<{ provider: string; count?: number }>;
  onChange: (provider: string | undefined) => void;
  className?: string;
}) {
  if (channels.length < 2) return null;
  const options = [
    { provider: undefined as string | undefined, label: "Todos" },
    ...channels.map((channel) => ({
      provider: channel.provider,
      label: NAMES[channel.provider] ?? channel.provider,
      count: channel.count,
    })),
  ];
  return (
    <div className={cn("flex flex-wrap gap-1", className)} role="group" aria-label="Canal">
      {options.map((option) => {
        const selected = (value ?? undefined) === option.provider;
        return (
          <button
            key={option.label}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.provider)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs transition-[background-color,border-color] duration-150 ease-out active:scale-[0.97]",
              selected
                ? "border-line-strong bg-surface-sunken text-ink"
                : "border-line bg-surface text-ink-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink",
            )}
          >
            {option.provider ? <ChannelMark provider={option.provider} /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function ChannelNotice({
  provider,
  children,
  className,
  ...props
}: ComponentProps<"p"> & { provider: string }) {
  return (
    <p
      className={cn(
        "flex items-start gap-2 rounded-control border border-info/25 bg-info-subtle px-3 py-2 text-2xs text-ink-secondary",
        className,
      )}
      {...props}
    >
      <ChannelMark provider={provider} className="mt-0.5" />
      <span>{children}</span>
    </p>
  );
}
